import { GlobalConfiguration } from '../../models/GlobalConfiguration';

export function buildGlobalConfigRequestHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export async function verifySignedGlobalConfiguration(
  data: unknown,
  signature: string,
  publicKey: string,
  verifySignedConfigData: (data: unknown, signature: string, publicKey: string) => Promise<boolean>,
): Promise<GlobalConfiguration> {
  if (!(await verifySignedConfigData(data, signature, publicKey))) {
    throw new Error('Invalid configuration signature');
  }
  return data as GlobalConfiguration;
}

/**
 * Fetches global configuration, falling back to signature verification when the
 * unverified read fails. Orchestration extracted from the non-admin BackendService.
 */
export async function fetchVerifiedGlobalConfiguration(deps: {
  withAuthToken: () => Promise<string | null>;
  authenticatedFetch: (url: string, init: { headers: Record<string, string> }) => Promise<Response>;
  apiURL: (path: string) => string;
  verifySignedConfigData: (data: unknown, signature: string, publicKey: string) => Promise<boolean>;
}): Promise<GlobalConfiguration> {
  const fetchConfig = async () => {
    const token = await deps.withAuthToken();
    const res = await deps.authenticatedFetch(deps.apiURL('globalConfig'), {
      headers: buildGlobalConfigRequestHeaders(token),
    });
    return res.json();
  };

  try {
    return (await fetchConfig()).data;
  } catch {
    const { data, signature, publicKey } = await fetchConfig();
    return verifySignedGlobalConfiguration(data, signature, publicKey, deps.verifySignedConfigData);
  }
}

export type CustodianStatus = {
  isCustodian: boolean;
  contactId: string;
  timestamp: string;
};

export async function verifyCustodianStatusPayload(
  data: CustodianStatus,
  signature: string,
  publicKeyBase64: string,
  verifySignedConfigData: (data: unknown, signature: string, publicKey: string) => Promise<boolean>,
  handleError: (body: { code: string; error: string }) => unknown,
): Promise<CustodianStatus> {
  if (publicKeyBase64 && publicKeyBase64.trim() !== '') {
    if (!(await verifySignedConfigData(data, signature, publicKeyBase64))) {
      throw handleError({
        code: 'SIGNATURE_VERIFICATION_FAILED',
        error: 'Invalid custodian status signature',
      });
    }
  }
  return data;
}

export function rethrowOrWrapCustodianStatusError(
  error: unknown,
  handleError: (body: { code: string; error: string }) => unknown,
): never {
  if (error && typeof error === 'object' && 'code' in error && 'error' in error) {
    throw error;
  }
  throw handleError({
    code: 'NETWORK_ERROR',
    error: 'Network error while fetching custodian status',
  });
}
